import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth, api } from '../context/AuthContext';
import { 
  Sparkles, LogOut, Home, Activity, Send, ChevronLeft, ChevronRight,
  CheckCircle, BookOpen, Loader2
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const TRACKER_STORAGE_KEY = 'hikari-body-compass-data-v2';
const TUTOR_STORAGE_KEY = 'hikari-tutor-state-v2';

const LESSONS = [
  { title: "What is functional medicine — and how it differs from conventional care", concepts: ["Functional Medicine", "Root Cause Model", "Systems Biology"] },
  { title: "The root-cause model — symptoms as signals, not diagnoses", concepts: ["Symptom as Signal", "Upstream Drivers", "Disease vs Dysfunction"] },
  { title: "Body systems thinking — nothing works in isolation", concepts: ["Systems Thinking", "Interconnection", "Biological Networks"] },
  { title: "The terrain — gut, hormones, inflammation, and the nervous system", concepts: ["Terrain Medicine", "Four Pillars", "Biological Environment"] },
  { title: "The gut — microbiome, leaky gut, and the gut-brain axis", concepts: ["Microbiome", "Leaky Gut", "Gut-Brain Axis"] },
  { title: "Hormones — thyroid, adrenal, and sex hormone interplay", concepts: ["Hormonal Cascade", "HPA Axis", "Thyroid Function"] },
  { title: "Inflammation — acute vs chronic, and why it drives everything", concepts: ["Acute Inflammation", "Chronic Inflammation", "NF-kB pathway"] },
  { title: "Blood sugar — glucose, insulin resistance, and energy regulation", concepts: ["Glucose Regulation", "Insulin Resistance", "Metabolic Syndrome"] },
  { title: "Mitochondria — energy production, oxidative stress, and fatigue", concepts: ["ATP Production", "Oxidative Stress", "Mitochondrial Dysfunction"] },
  { title: "Detoxification — liver pathways, methylation, and toxic load", concepts: ["Phase I/II Detox", "Methylation", "Glutathione"] },
  { title: "Fatigue — mapping energy depletion to root causes", concepts: ["Fatigue Mapping", "Adrenal Patterns", "Thyroid-Fatigue Link"] },
  { title: "Brain fog — neuroinflammation, blood sugar, and nutrient gaps", concepts: ["Neuroinflammation", "Blood-Brain Barrier", "Brain Fog Drivers"] },
  { title: "Sleep dysregulation — cortisol, melatonin, and circadian rhythms", concepts: ["Circadian Rhythm", "Cortisol Curve", "Sleep Architecture"] },
  { title: "Histamine — reactivity, mast cells, and sensitivity patterns", concepts: ["Histamine Intolerance", "Mast Cell Activation", "DAO Enzyme"] },
  { title: "Women's health — cycle, perimenopause, and hormonal terrain", concepts: ["Cycle Mapping", "Estrogen Dominance", "Perimenopause"] },
  { title: "Reading labs differently — optimal vs reference ranges", concepts: ["Optimal Ranges", "Functional Lab Interpretation", "Lab Patterns"] },
  { title: "Thyroid panel — TSH, free T3, free T4, reverse T3, antibodies", concepts: ["TSH", "Free T3/T4", "Thyroid Antibodies"] },
  { title: "Nutrient markers — B12, folate, ferritin, D3, zinc, magnesium", concepts: ["Nutrient Depletion", "B12 Methylation", "Vitamin D Testing"] },
  { title: "Inflammatory markers — CRP, ESR, homocysteine, fibrinogen", concepts: ["High-Sensitivity CRP", "ESR", "Homocysteine"] },
  { title: "Metabolic panel — glucose, HbA1c, insulin, lipids in context", concepts: ["HbA1c", "Fasting Insulin", "Metabolic Health"] },
  { title: "Food as medicine — anti-inflammatory eating principles", concepts: ["Anti-Inflammatory Diet", "Elimination Protocol", "Food Sensitivities"] },
  { title: "Supplements — what works, what doesn't, and sequencing", concepts: ["Evidence-Based Supplements", "Supplement Sequencing", "Bioavailability"] },
  { title: "Stress and the nervous system — HPA axis and recovery", concepts: ["HPA Axis", "Cortisol Dysregulation", "Nervous System Recovery"] },
  { title: "Sleep as medicine — protocols, timing, and light hygiene", concepts: ["Sleep Hygiene", "Blue Light", "Sleep Protocols"] },
  { title: "Movement — when it heals and when it depletes", concepts: ["Hormetic Stress", "Zone 2 Training", "Exercise and Inflammation"] },
  { title: "Ayurveda meets functional medicine — shared roots", concepts: ["Ayurvedic Philosophy", "Prana", "Wholistic Systems"] },
  { title: "Doshas and metabolic types — constitution as baseline", concepts: ["Vata/Pitta/Kapha", "Prakriti", "Constitutional Medicine"] },
  { title: "Adaptogens and herbs — ashwagandha, tulsi, shatavari, and more", concepts: ["Adaptogens", "Ashwagandha", "Rasayanas"] },
];

export default function TutorPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const messagesEndRef = useRef(null);

  const [currentLesson, setCurrentLesson] = useState(0);
  const [completedLessons, setCompletedLessons] = useState(new Set());
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [trackerContext, setTrackerContext] = useState(null);

  // Load saved state
  useEffect(() => {
    try {
      const saved = localStorage.getItem(TUTOR_STORAGE_KEY);
      if (saved) {
        const state = JSON.parse(saved);
        setCurrentLesson(state.currentLesson || 0);
        setCompletedLessons(new Set(state.completedLessons || []));
        setMessages(state.messages || []);
      }
    } catch (e) {
      console.error('Failed to load tutor state:', e);
    }

    // Load tracker context
    try {
      const trackerData = localStorage.getItem(TRACKER_STORAGE_KEY);
      if (trackerData) {
        setTrackerContext(JSON.parse(trackerData));
      }
    } catch (e) {
      console.error('Failed to load tracker context:', e);
    }
  }, []);

  // Save state on changes
  useEffect(() => {
    localStorage.setItem(TUTOR_STORAGE_KEY, JSON.stringify({
      currentLesson,
      completedLessons: Array.from(completedLessons),
      messages
    }));
  }, [currentLesson, completedLessons, messages]);

  // Scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const buildSystemPrompt = () => {
    const lesson = LESSONS[currentLesson];
    let trackerInfo = '';
    
    if (trackerContext) {
      const m = trackerContext.metrics || {};
      const symptoms = trackerContext.symptoms?.join(', ') || 'none logged';
      trackerInfo = `\n\nThe user's Body Compass tracker shows: Energy ${m.energy}/10, Sleep ${m.sleep}/10, Digestion ${m.digestion}/10, Inflammation ${m.inflammation}/10, Mental clarity ${m.clarity}/10, Nervous system ease ${m.nervous}/10. Active symptoms today: ${symptoms}. When relevant, connect lesson content to these real signals.`;
    }

    return `You are Hikari AI — a warm, brilliant functional medicine guide built into the Hikari Body Compass health app. You are not a doctor and always make this clear when relevant, but you are deeply knowledgeable about functional medicine, systems biology, Ayurveda, nutritional science, and lab interpretation.

Current lesson: "${lesson.title}"
Key concepts to cover: ${lesson.concepts.join(', ')}

Teaching style:
- Warm, intelligent, direct — like a knowledgeable friend who happens to be a functional medicine practitioner
- Use analogies generously — make complex systems feel intuitive
- When a concept appears, give it a name and define it clearly
- Connect abstract concepts to real body sensations and experiences
- End responses with either a thought-provoking question OR a clear "what to understand next"
- Keep responses focused — teach one idea well rather than five ideas superficially
- Use **bold** for key terms, paragraph breaks for readability${trackerInfo}

Important: You are an educational tool. Always remind users to work with a qualified practitioner for personal health decisions.`;
  };

  const sendMessage = async (text) => {
    if (!text.trim() || isLoading) return;

    const userMessage = { role: 'user', content: text };
    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsLoading(true);

    try {
      const response = await api.post('/api/tutor/chat-with-log', {
        message: text,
        session_id: `lesson-${currentLesson}`,
        lesson_index: currentLesson,
        system_prompt: buildSystemPrompt()
      });

      const assistantMessage = { role: 'assistant', content: response.data.response };
      setMessages(prev => [...prev, assistantMessage]);
    } catch (error) {
      console.error('Chat error:', error);
      const errorMessage = { 
        role: 'assistant', 
        content: 'I had trouble responding. Please try again or check your connection.' 
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const switchLesson = (index) => {
    if (currentLesson !== index) {
      setCompletedLessons(prev => new Set([...prev, currentLesson]));
      setMessages([]);
    }
    setCurrentLesson(index);
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const formatMessage = (text) => {
    return text
      .replace(/\*\*(.*?)\*\*/g, '<strong class="text-[#2DD4BF]">$1</strong>')
      .replace(/\n\n/g, '</p><p class="mt-3">')
      .replace(/\n/g, '<br />');
  };

  const quickPrompts = [
    `Start lesson ${currentLesson + 1} — teach me about ${LESSONS[currentLesson].title.split('—')[0].trim()}`,
    "How does this connect to my Body Compass tracker?",
    "Give me a simple analogy I can remember",
    "What are the key takeaways?"
  ];

  const progressPercent = Math.round((completedLessons.size / LESSONS.length) * 100);

  return (
    <div className="bg-hikari min-h-screen flex">
      <div className="orb orb-mint"></div>
      <div className="orb orb-violet"></div>

      {/* Sidebar */}
      <aside className={`fixed md:relative z-40 h-screen transition-all duration-300 ${sidebarOpen ? 'w-72' : 'w-0 md:w-16'} flex-shrink-0`}>
        <div className={`glass h-full flex flex-col ${sidebarOpen ? 'opacity-100' : 'opacity-0 md:opacity-100'}`}>
          {/* Sidebar Header */}
          <div className="p-4 border-b border-white/5">
            <div className="flex items-center justify-between">
              {sidebarOpen && (
                <div>
                  <div className="font-semibold">Functional Medicine</div>
                  <div className="text-xs text-[#94A3B8]">{LESSONS.length} lessons</div>
                </div>
              )}
              <button 
                onClick={() => setSidebarOpen(!sidebarOpen)}
                className="p-2 rounded-lg hover:bg-white/5 text-[#94A3B8]"
                data-testid="toggle-sidebar"
              >
                {sidebarOpen ? <ChevronLeft className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
              </button>
            </div>
            
            {sidebarOpen && (
              <div className="mt-4">
                <div className="flex items-center justify-between text-xs text-[#94A3B8] mb-2">
                  <span>Progress</span>
                  <span>{completedLessons.size}/{LESSONS.length}</span>
                </div>
                <div className="progress-bar h-2">
                  <div className="h-full rounded-full progress-bar-mint" style={{ width: `${progressPercent}%` }} />
                </div>
              </div>
            )}
          </div>

          {/* Lessons List */}
          {sidebarOpen && (
            <div className="flex-1 overflow-y-auto p-3 space-y-1">
              {LESSONS.map((lesson, i) => (
                <button
                  key={i}
                  onClick={() => switchLesson(i)}
                  className={`w-full text-left p-3 rounded-xl text-sm transition-colors ${
                    i === currentLesson 
                      ? 'bg-[#2DD4BF]/10 border border-[#2DD4BF]/20 text-[#2DD4BF]' 
                      : 'hover:bg-white/5 text-[#94A3B8]'
                  }`}
                  data-testid={`lesson-${i}`}
                >
                  <div className="flex items-start gap-2">
                    <span className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                      {completedLessons.has(i) ? (
                        <CheckCircle className="w-4 h-4 text-[#2DD4BF]" />
                      ) : (
                        <span className="text-xs font-medium opacity-60">{i + 1}</span>
                      )}
                    </span>
                    <span className="line-clamp-2">{lesson.title}</span>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-h-screen content-wrapper">
        {/* Header */}
        <header className="glass mx-4 mt-4 px-5 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-[#2DD4BF] to-[#3B82F6] flex items-center justify-center">
              <Sparkles className="w-4 h-4 text-[#0A0B10]" />
            </div>
            <div>
              <div className="kicker text-[#2DD4BF] text-[10px]">Hikari Health</div>
              <div className="text-sm font-semibold">AI Tutor</div>
            </div>
          </div>
          
          <nav className="flex items-center gap-2">
            <Link to="/" className="p-2 rounded-lg hover:bg-white/5 text-[#94A3B8] hover:text-white" data-testid="nav-home">
              <Home className="w-5 h-5" />
            </Link>
            <Link to="/tracker" className="p-2 rounded-lg hover:bg-white/5 text-[#94A3B8] hover:text-white" data-testid="nav-tracker">
              <Activity className="w-5 h-5" />
            </Link>
            <button onClick={handleLogout} className="p-2 rounded-lg hover:bg-white/5 text-[#94A3B8] hover:text-white" data-testid="logout-button">
              <LogOut className="w-5 h-5" />
            </button>
          </nav>
        </header>

        {/* Chat Area */}
        <div className="flex-1 flex flex-col mx-4 my-4 glass overflow-hidden">
          {/* Lesson Banner */}
          <div className="p-4 border-b border-white/5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#2DD4BF] to-[#3B82F6] flex items-center justify-center">
                <BookOpen className="w-5 h-5 text-[#0A0B10]" />
              </div>
              <div>
                <div className="text-sm text-[#94A3B8]">Lesson {currentLesson + 1}</div>
                <div className="font-semibold">{LESSONS[currentLesson].title}</div>
              </div>
            </div>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4" data-testid="chat-messages">
            {messages.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-center p-6">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#2DD4BF] to-[#3B82F6] flex items-center justify-center mb-4">
                  <Sparkles className="w-8 h-8 text-[#0A0B10]" />
                </div>
                <h3 className="text-xl font-semibold mb-2">Welcome to Hikari AI</h3>
                <p className="text-[#94A3B8] max-w-md mb-6">
                  I'm your functional medicine guide. Let's explore <strong className="text-white">{LESSONS[currentLesson].title.split('—')[0].trim()}</strong> together.
                </p>
                <div className="text-sm text-[#475569]">
                  Click a quick prompt below or type your own question.
                </div>
              </div>
            ) : (
              messages.map((msg, i) => (
                <div key={i} className={`flex gap-3 ${msg.role === 'user' ? 'flex-row-reverse' : ''}`}>
                  <div className={`w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0 ${
                    msg.role === 'assistant' 
                      ? 'bg-gradient-to-br from-[#2DD4BF] to-[#3B82F6]' 
                      : 'bg-white/10'
                  }`}>
                    {msg.role === 'assistant' ? (
                      <Sparkles className="w-4 h-4 text-[#0A0B10]" />
                    ) : (
                      <span className="text-xs font-medium">{user?.name?.[0] || 'U'}</span>
                    )}
                  </div>
                  <div 
                    className={`msg-bubble ${msg.role === 'assistant' ? 'hikari' : 'user'}`}
                    dangerouslySetInnerHTML={{ __html: formatMessage(msg.content) }}
                  />
                </div>
              ))
            )}
            
            {isLoading && (
              <div className="flex gap-3">
                <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-[#2DD4BF] to-[#3B82F6] flex items-center justify-center flex-shrink-0">
                  <Sparkles className="w-4 h-4 text-[#0A0B10]" />
                </div>
                <div className="msg-bubble hikari">
                  <div className="flex gap-1">
                    <div className="typing-dot"></div>
                    <div className="typing-dot"></div>
                    <div className="typing-dot"></div>
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input Area */}
          <div className="p-4 border-t border-white/5">
            {/* Quick Prompts */}
            <div className="flex flex-wrap gap-2 mb-3">
              {quickPrompts.map((prompt, i) => (
                <button
                  key={i}
                  onClick={() => sendMessage(prompt)}
                  disabled={isLoading}
                  className="px-3 py-1.5 text-xs rounded-full border border-white/10 bg-white/3 text-[#94A3B8] hover:bg-white/5 hover:text-white hover:border-white/20 transition-colors disabled:opacity-50"
                  data-testid={`quick-prompt-${i}`}
                >
                  {prompt.length > 40 ? prompt.slice(0, 40) + '...' : prompt}
                </button>
              ))}
            </div>

            {/* Input */}
            <div className="flex gap-3">
              <input
                type="text"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && sendMessage(inputValue)}
                placeholder="Ask Hikari AI anything about functional medicine..."
                className="flex-1"
                disabled={isLoading}
                data-testid="chat-input"
              />
              <button
                onClick={() => sendMessage(inputValue)}
                disabled={isLoading || !inputValue.trim()}
                className="btn-primary w-12 h-12 flex items-center justify-center disabled:opacity-50"
                data-testid="send-button"
              >
                {isLoading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <Send className="w-5 h-5" />
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
